import { Dialog } from '@primer/react/experimental';
import styled from 'styled-components';
import { useDmaCompanyPaymentPrice } from '../../consultant-dashboard/company-creation-wizard/use-dma-company-payment-price';
import { formatPriceLabel, CheckIcon } from './pricing-utils';
import { EmptyHeader } from './shared-modal-components';
import { usePushAnonymousGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { useNavigate } from 'react-router-dom';
import { LeadFormId, getLeadForm } from '@upright/ui/common/activities/lead-form/types';
import { useCsrdCompanyContext } from '../../../../common/upright/model/output/scores/csrd/csrd-company-double-materiality/context';

interface TierSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPro: () => void;
  companyName: string;
  organizationName: string;
}

export function TierSelectionModal({
  isOpen,
  onClose,
  onSelectPro,
  companyName,
  organizationName,
}: TierSelectionModalProps) {
  const { data: paymentPrice } = useDmaCompanyPaymentPrice();
  const priceLabel = paymentPrice
    ? formatPriceLabel({ amount: paymentPrice.amount, currency: paymentPrice.currency })
    : undefined;
  const pushGtmEvent = usePushAnonymousGtmEvent();
  const navigate = useNavigate();
  const { companyId } = useCsrdCompanyContext();

  if (!isOpen) return null;

  const handleEnterprise = () => {
    onClose();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    pushGtmEvent({ event: getLeadForm(LeadFormId.BOOK_DEMO_CSRD).openEvent as any });
    // Navigate to the company-specific dashboard with the lead form modal, not the org-level dashboard
    navigate(`/company/${companyId}/${encodeURIComponent(companyName)}/dashboard?lfid=book-demo-csrd`);
  };

  return (
    <WideDialog title="Upgrade DMA assessment" onClose={onClose} width="xlarge" renderHeader={EmptyHeader}>
      <Content>
        <Header>
          <Title>Upgrade DMA assessment</Title>
          <TextBlock>Upgrade a DMA assessment for a client company under your consultancy account.</TextBlock>
          <TextBlockStrong>Client company: {companyName || '—'}</TextBlockStrong>
          <TextBlockStrong>Account: {organizationName || '—'}</TextBlockStrong>
        </Header>
        <Cards>
          <Card>
            <CardTop>
              <CardTitle>Self serve DMA assessment</CardTitle>
              <CardSubtitle>Pro tier</CardSubtitle>
              <PriceRow>
                <PriceValue>{priceLabel ?? '—'}</PriceValue>
                <PriceDescription>single purchase, per client company</PriceDescription>
              </PriceRow>
            </CardTop>
            <PrimaryButton type="button" onClick={onSelectPro}>
              Upgrade to Pro
            </PrimaryButton>
            <CardFeatures>
              <SectionTitle>What's included</SectionTitle>
              <List>
                <ListItem>
                  <CheckIcon />
                  <span>ESRS-aligned DMA results</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Clear visual result overview</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Integrate own insights to results</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Add your own input data</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Freeze and save result snapshots (coming soon)</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Invite team members to collaborate</span>
                </ListItem>
              </List>
              <SectionTitle>Access</SectionTitle>
              <List>
                <ListItem>
                  <CheckIcon />
                  <span>12 month access to data with an option to renew</span>
                </ListItem>
              </List>
            </CardFeatures>
          </Card>
          <Card>
            <CardTop>
              <CardTitle>CSRD for large organizations</CardTitle>
              <CardSubtitle>Enterprise tier</CardSubtitle>
              <CardNote>Talk to sales</CardNote>
            </CardTop>
            <SecondaryButton type="button" onClick={handleEnterprise}>
              Request an enterprise assessment
            </SecondaryButton>
            <CardFeatures>
              <SectionTitle>Everything in pro tier, plus</SectionTitle>
              <List>
                <ListItem>
                  <CheckIcon />
                  <span>Financial effects of risks and opportunities</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>More detailed results and evidence</span>
                </ListItem>
                <ListItem>
                  <CheckIcon />
                  <span>Premium customer support, including assurance support</span>
                </ListItem>
              </List>
              <SectionTitle>Best for</SectionTitle>
              <List>
                <ListItem>
                  <CheckIcon />
                  <span>Group-wide DMA and large client organizations</span>
                </ListItem>
              </List>
            </CardFeatures>
          </Card>
        </Cards>
      </Content>
    </WideDialog>
  );
}

// Override Primer Dialog to match the company-creation wizard modal dimensions.
// We target the dialog element itself for width (Primer's default is 640px),
// and the inner overflow wrapper for height/layout.
const WideDialog = styled(Dialog)`
  &[role='dialog'] {
    width: 1280px;
    min-width: 640px;
    max-width: calc(100dvw - 64px);
    border-radius: var(--border-radius__xl);
  }

  &[role='dialog'] > div:first-child {
    height: auto;
    min-height: 600px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  padding: 48px;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  overflow-y: auto;
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const Title = styled.h2`
  margin: 0;
  margin-bottom: 36px;
  font-size: 24px;
  font-weight: 700;
`;

const TextBlock = styled.p`
  margin: 0;
  font-size: 15px;
  color: ${props => props.theme.colors.fg.default};
  line-height: 1.6;
`;

const TextBlockStrong = styled(TextBlock)`
  font-weight: 600;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 1281px) {
    padding-bottom: 48px;
  }
`;

/** Each Card has exactly 3 direct children: CardTop, Button, CardFeatures.
 *  Using subgrid so both cards share the same row tracks, aligning the buttons
 *  at the same vertical position regardless of differing CardTop heights.
 *
 *  A data card by DESIGN.md's two-variant table: border-only, `xl` padding, no shadow, and not
 *  itself a click target — the tier is chosen by the Button inside, not by pressing the card. So
 *  `lg`, not the `xl` radius an action card takes. Identical to the card in the company-creation
 *  wizard's payment step; if a shared Card component ever lands, both are call sites for it. */
const Card = styled.div`
  border: 1px solid var(--borderColor-default);
  border-radius: var(--border-radius__lg);
  padding: var(--space__xl);
  display: grid;
  grid-row: span 3;
  grid-template-rows: subgrid;
  gap: 24px;
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/** Wraps the feature lists below the button so each card has exactly 3 grid children. */
const CardFeatures = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
`;

const CardSubtitle = styled.p`
  margin: 0;
  color: ${props => props.theme.colors.fg.muted};
  font-size: 14px;
`;

const CardNote = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.colors.fg.default};
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--space__sm);
`;

const PriceValue = styled.div`
  font-size: 36px;
  font-weight: 400;
`;

const PriceDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${props => props.theme.colors.fg.muted};
`;

const SectionTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  display: grid;
  gap: 20px;
  color: ${props => props.theme.colors.fg.default};
  font-size: 14px;
  list-style: none;

  li {
    list-style: none;
  }
`;

const ListItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  height: 36px;
  align-self: end;
  border-radius: 6px;
  border: 1px solid var(--color__lilac-500);
  background: var(--color__lilac-500);
  color: white;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--color__lilac-600);
    border-color: var(--color__lilac-600);
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  height: 36px;
  align-self: end;
  border-radius: 6px;
  border: 1px solid var(--borderColor-default);
  background: transparent;
  font-weight: 600;
  cursor: pointer;
`;
